import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../apiProperties';
import { logAudit } from './audit';

const KEY = 'scheduledDeploys:v1';
const SYNC_EVENT = 'scheduledDeploysChanged';

// Schedules more than this many ms overdue auto-cancel instead of firing —
// it's almost always because the dashboard was closed when the timer
// elapsed, and an operator who comes back tomorrow doesn't want a surprise
// midnight deploy.
const FIRE_TOLERANCE_MS = 5 * 60 * 1000;

// Custom event raised whenever the runner finishes firing (or failing, or
// auto-cancelling) a scheduled deploy. The DeploySchedulerComponent listens
// for it to flash a snackbar; other consumers can subscribe too.
export const SCHEDULED_DEPLOY_FIRED_EVENT = 'fsam:scheduled-deploy-fired';
export interface ScheduledDeployFiredDetail {
  schedule: ScheduledDeploy;
  outcome: 'completed' | 'failed' | 'cancelled';
  message?: string;
}

export type ScheduledDeploy = {
  id: string;
  createdAt: number;
  scheduledFor: number; // epoch ms
  isPatch: boolean;
  version: string;
  apps: Array<{
    appId: number | null;
    composerHost: string;
    composerAppId: string | number;
    platform: string;
    appName: string;
    appVersion: string;
    generateAab?: boolean;
    uploadScreenshot?: boolean;
  }>;
  notes?: string;
  status: 'pending' | 'firing' | 'completed' | 'failed' | 'cancelled';
  // Set when the timer fires the trigger — useful for the activity log.
  firedAt?: number;
  completedAt?: number;
  errorMessage?: string;
};

const read = (): ScheduledDeploy[] => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

const write = (list: ScheduledDeploy[]) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  } catch { /* quota — ignore */ }
};

export function useScheduledDeploys() {
  const [list, setList] = useState<ScheduledDeploy[]>(read);

  useEffect(() => {
    const sync = () => setList(read());
    const onStorage = (e: StorageEvent) => { if (!e.key || e.key === KEY) sync(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SYNC_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SYNC_EVENT, sync as EventListener);
    };
  }, []);

  const add = useCallback((entry: ScheduledDeploy) => {
    const next = [...read(), entry].sort((a, b) => a.scheduledFor - b.scheduledFor);
    write(next);
  }, []);

  const update = useCallback((id: string, patch: Partial<ScheduledDeploy>) => {
    const next = read().map(e => e.id === id ? { ...e, ...patch } : e);
    write(next);
  }, []);

  const remove = useCallback((id: string) => {
    write(read().filter(e => e.id !== id));
  }, []);

  return { list, add, update, remove };
}

// ---------------------------------------------------------------------------
// useScheduledDeployRunner — app-level firing loop.
//
// Mount this ONCE at the dashboard shell (HomeComponent) so scheduled deploys
// fire regardless of which sub-page the operator is currently looking at. The
// previous design lived inside DeploySchedulerComponent's useEffect, which
// meant a schedule the operator created and then navigated away from would
// just sit in `pending` forever — the timer only ticked while the Deploy
// Scheduler page was mounted.
//
// Safety properties:
//   • Reentrancy guard: a per-tab in-flight Set blocks the same schedule
//     from being picked up twice within a single tick if the tick takes
//     longer than the interval.
//   • Status-based locking: the first thing we do for an eligible schedule
//     is flip its status to 'firing' via the synchronous localStorage write,
//     so any other tab's runner reading the data sees the new status and
//     skips. Two tabs firing the same schedule within the same 15 s tick
//     remains theoretically possible but is harmless on the worker side
//     (the trigger endpoint is content-addressable enough that you'd get
//     duplicate pipelines rather than a corrupted run; we accept that
//     for the simplicity).
//   • Overdue auto-cancel: anything more than FIRE_TOLERANCE_MS past due
//     flips to 'cancelled' with a clear errorMessage instead of firing.
// ---------------------------------------------------------------------------
const fireDueOnce = async () => {
  const now = Date.now();
  const due = read().filter(s => s.status === 'pending' && s.scheduledFor <= now);
  if (due.length === 0) return;

  const updateInner = (id: string, patch: Partial<ScheduledDeploy>) => {
    const next = read().map(e => e.id === id ? { ...e, ...patch } : e);
    write(next);
  };

  const emit = (detail: ScheduledDeployFiredDetail) => {
    try { window.dispatchEvent(new CustomEvent(SCHEDULED_DEPLOY_FIRED_EVENT, { detail })); } catch { /* ignore */ }
  };

  for (const sched of due) {
    if (now - sched.scheduledFor > FIRE_TOLERANCE_MS) {
      const overdueMin = Math.round((now - sched.scheduledFor) / 60000);
      const errorMessage = `Skipped — schedule was ${overdueMin} minutes overdue (likely the dashboard wasn't open).`;
      updateInner(sched.id, { status: 'cancelled', completedAt: now, errorMessage });
      logAudit({
        action: 'schedule_cancel',
        targetType: 'schedule',
        targetId: sched.id,
        metadata: { reason: 'overdue', scheduledFor: new Date(sched.scheduledFor).toISOString() },
      });
      emit({ schedule: sched, outcome: 'cancelled', message: errorMessage });
      continue;
    }

    // Claim the schedule by flipping its status BEFORE firing. Any other
    // tab's runner that reads localStorage will see 'firing' and skip it.
    updateInner(sched.id, { status: 'firing', firedAt: now });

    try {
      const tok = localStorage.getItem('token');
      const payloadData = sched.apps.map(a => {
        const payload: any = {
          composerAppId: a.composerAppId,
          composerHost: a.composerHost,
          platform: a.platform,
          app_name: a.appName,
          version: sched.version,
          domain: a.composerHost,
        };
        // Only send a real D1 id. Never id:0/null — the backend resolves the app
        // by composerHost + platform + composerAppId when the id is absent.
        if (a.appId != null) payload.id = a.appId;
        if (a.platform === 'android' && a.generateAab) payload.generateAab = true;
        if (a.platform === 'ios' && a.uploadScreenshot) payload.uploadScreenshot = true;
        return payload;
      });
      await axios.post(API.trigger, {
        selectedRows: payloadData,
        incrementPatch: sched.isPatch,
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
      });
      updateInner(sched.id, { status: 'completed', completedAt: Date.now() });
      logAudit({
        action: 'schedule_fire',
        targetType: 'schedule',
        targetId: sched.id,
        metadata: {
          isPatch: sched.isPatch,
          version: sched.version,
          appCount: sched.apps.length,
          scheduledFor: new Date(sched.scheduledFor).toISOString(),
          firedAt: new Date().toISOString(),
        },
      });
      // OS-level notification. Works regardless of which page the operator
      // is currently looking at — that's the whole point of moving the
      // runner up to the app shell.
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('📅 Scheduled deploy triggered', {
            body: `${sched.apps.length} app${sched.apps.length !== 1 ? 's' : ''} · ${sched.isPatch ? 'patch' : `v${sched.version}`}`,
            tag: `sched-${sched.id}`,
          });
        } catch { /* OS denied — already covered by the bell flow */ }
      }
      emit({
        schedule: sched,
        outcome: 'completed',
        message: `Scheduled deploy fired (${sched.apps.length} app${sched.apps.length !== 1 ? 's' : ''}).`,
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Trigger failed';
      updateInner(sched.id, { status: 'failed', completedAt: Date.now(), errorMessage });
      emit({ schedule: sched, outcome: 'failed', message: errorMessage });
    }
  }
};

// Module-level reentrancy guard so two simultaneous mounts of the runner
// hook (e.g. StrictMode double-effect in dev, or two routes that both happen
// to mount it) can't run the firing loop concurrently within the same tab.
let tickInFlight = false;
const safeTick = async () => {
  if (tickInFlight) return;
  tickInFlight = true;
  try { await fireDueOnce(); }
  finally { tickInFlight = false; }
};

/**
 * Drive the scheduled-deploy timer. Mount once at the dashboard shell.
 *
 * Returns nothing — the hook only owns the timer and side effects. UI updates
 * happen via the existing localStorage + SYNC_EVENT plumbing that
 * `useScheduledDeploys` already subscribes to.
 */
export function useScheduledDeployRunner(): void {
  // Keep a ref to detect cancellation between async ticks.
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    // Fire once on mount so a schedule due in the past minute doesn't have
    // to wait the full interval to be picked up.
    safeTick();
    const id = setInterval(() => { if (!cancelledRef.current) safeTick(); }, 15_000);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
    };
  }, []);
}
